'use client';
import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { Search, Send, Paperclip, Phone, MoreVertical, Check, CheckCheck, Clock, MessageSquare, X, FileText, Zap, ArrowDownLeft, ArrowUpRight, RefreshCw, Globe, AlertCircle, Reply, ChevronDown } from 'lucide-react';
import StageBadge from '@/components/ui/StageBadge';

import { toast } from 'sonner';
import { useRealtimeLeads, type LeadStage } from '@/hooks/useRealtimeLeads';
import { useRealtimeChat, type ChatMessage } from '@/hooks/useRealtimeChat';
import { createClient } from '@/lib/supabase/client';

// Remove the hardcoded templates array — replaced by DB fetch below

interface LastMsgEntry {
  text: string;
  direction: 'in' | 'out';
  createdAt: string;
}

interface TwilioApiMessage {
  id: string;
  sid: string;
  body: string;
  from: string;
  to: string;
  direction: 'in' | 'out';
  status: string;
  createdAt: string;
  dateSent: string | null;
}

const TWILIO_NUMBER = process.env.NEXT_PUBLIC_TWILIO_WHATSAPP_NUMBER ?? '';

// ── Hook: fetch messages directly from Twilio API ──
function useTwilioMessages() {
  const [messages, setMessages] = useState<TwilioApiMessage[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastFetched, setLastFetched] = useState<Date | null>(null);

  const fetchTwilioMsgs = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch('/api/twilio/messages?pageSize=1000');
      if (!res.ok) {
        const errData = await res.json().catch(() => ({ error: `HTTP ${res.status}` })) as { error?: string };
        setError(errData.error ?? `HTTP error ${res.status}`);
        return;
      }
      const data = await res.json() as { success: boolean; messages?: TwilioApiMessage[]; error?: string };
      if (data.success && data.messages) {
        setMessages(data.messages);
        setLastFetched(new Date());
      } else {
        setError(data.error ?? 'Failed to fetch messages');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Network error');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTwilioMsgs();
    // Auto-refresh every 30 seconds
    const interval = setInterval(fetchTwilioMsgs, 30000);
    return () => clearInterval(interval);
  }, [fetchTwilioMsgs]);

  return { messages, loading, error, refetch: fetchTwilioMsgs, lastFetched };
}

const StatusBadge = ({ status, direction }: { status: ChatMessage['status']; direction: 'in' | 'out' }) => {
  if (direction === 'in') {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200">
        <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 inline-block" />
        Received
      </span>
    );
  }
  const map: Record<string, { label: string; color: string; dot: string }> = {
    pending: { label: 'Pending', color: 'bg-amber-50 text-amber-700 border-amber-200', dot: 'bg-amber-400' },
    sent: { label: 'Sent', color: 'bg-sky-50 text-sky-700 border-sky-200', dot: 'bg-sky-500' },
    delivered: { label: 'Delivered', color: 'bg-blue-50 text-blue-700 border-blue-200', dot: 'bg-blue-500' },
    read: { label: 'Read', color: 'bg-violet-50 text-violet-700 border-violet-200', dot: 'bg-violet-500' },
    queued: { label: 'Queued', color: 'bg-amber-50 text-amber-700 border-amber-200', dot: 'bg-amber-400' },
    failed: { label: 'Failed', color: 'bg-red-50 text-red-700 border-red-200', dot: 'bg-red-500' },
    undelivered: { label: 'Undelivered', color: 'bg-red-50 text-red-700 border-red-200', dot: 'bg-red-500' },
  };
  const s = map[status] ?? map.sent;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold border ${s.color}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${s.dot} inline-block`} />
      {s.label}
    </span>
  );
};

const StatusIcon = ({ status }: { status: ChatMessage['status'] }) => {
  if (status === 'pending') return <Clock size={11} className="text-amber-400" />;
  if (status === 'sent') return <Check size={11} className="text-sky-400" />;
  if (status === 'delivered') return <CheckCheck size={11} className="text-blue-400" />;
  return <CheckCheck size={11} className="text-violet-500" />;
};

function formatMsgTime(dateStr: string): string {
  const d = new Date(dateStr);
  return d.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true });
}

function formatMsgDateTime(dateStr: string): string {
  const d = new Date(dateStr);
  return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) +
    ' ' + d.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true });
}

function formatMsgDate(dateStr: string): string {
  const d = new Date(dateStr);
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);
  if (d.toDateString() === today.toDateString()) return 'Today';
  if (d.toDateString() === yesterday.toDateString()) return 'Yesterday';
  return d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
}

function formatPhone(phone: string): string {
  if (!phone) return '';
  const cleaned = phone.replace(/\s/g, '');
  if (cleaned.startsWith('+91')) return cleaned;
  if (cleaned.startsWith('91') && cleaned.length > 10) return `+${cleaned}`;
  return `+91${cleaned}`;
}

function groupMessagesByDate(messages: ChatMessage[]): { date: string; msgs: ChatMessage[] }[] {
  const groups: { date: string; msgs: ChatMessage[] }[] = [];
  let currentDate = '';
  for (const msg of messages) {
    const d = formatMsgDate(msg.createdAt);
    if (d !== currentDate) {
      currentDate = d;
      groups.push({ date: d, msgs: [msg] });
    } else {
      groups[groups.length - 1].msgs.push(msg);
    }
  }
  return groups;
}

type ViewMode = 'chat' | 'log';
type MainTab = 'conversations' | 'all';

export default function WhatsAppChatClient() {
  const { leads, loading: leadsLoading } = useRealtimeLeads();
  const [activeLeadId, setActiveLeadId] = useState<string | null>(null);
  const [input, setInput] = useState('');
  const [showTemplates, setShowTemplates] = useState(false);
  const [activeTemplateName, setActiveTemplateName] = useState<string | null>(null);
  const [searchConv, setSearchConv] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [lastMsgMap, setLastMsgMap] = useState<Record<string, LastMsgEntry>>({});
  const [viewMode, setViewMode] = useState<ViewMode>('log');
  const [filterDir, setFilterDir] = useState<'all' | 'in' | 'out'>('all');
  const [mainTab, setMainTab] = useState<MainTab>('all');
  const [allMsgSearch, setAllMsgSearch] = useState('');
  const [allMsgFilter, setAllMsgFilter] = useState<'all' | 'in' | 'out'>('all');
  const [leadTagFilter, setLeadTagFilter] = useState<'all' | 'drip' | 'organic'>('all');
  const [hideUndelivered, setHideUndelivered] = useState(true);
  // dripPhones: normalized last-10-digit strings of phones enrolled in any drip campaign
  const [dripPhones, setDripPhones] = useState<Set<string>>(new Set());
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Reply state for All Messages tab
  const [replyTarget, setReplyTarget] = useState<TwilioApiMessage | null>(null);
  const [replyTemplateKey, setReplyTemplateKey] = useState<string>('');
  const [isReplying, setIsReplying] = useState(false);
  const replyFileInputRef = useRef<HTMLInputElement>(null);
  const [replyAttachment, setReplyAttachment] = useState<File | null>(null);
  const [isUploadingMedia, setIsUploadingMedia] = useState(false);

  // Approved templates from DB
  const [approvedTemplates, setApprovedTemplates] = useState<{ id: string; name: string; label: string; body: string; content_sid: string | null }[]>([]);

  useEffect(() => {
    const supabase = createClient();
    supabase
      .from('whatsapp_templates')
      .select('id, name, label, body, content_sid')
      .eq('status', 'Approved')
      .order('label', { ascending: true })
      .then(({ data }) => {
        if (data) setApprovedTemplates(data);
      });

    // Fetch drip-enrolled phones — use lead_phone directly so we can match
    // against Twilio message phone numbers without needing a lead-ID lookup.
    supabase
      .from('drip_message_queue')
      .select('lead_phone')
      .then(({ data }) => {
        if (data) {
          const phones = new Set<string>();
          for (const r of data as { lead_phone: string }[]) {
            if (r.lead_phone) {
              // Store last 10 digits for flexible matching
              const digits = r.lead_phone.replace(/\D/g, '');
              phones.add(digits.slice(-10));
            }
          }
          setDripPhones(phones);
        }
      });
  }, []);

  const { messages, loading: msgsLoading, sendMessage, refetch } = useRealtimeChat(activeLeadId);
  const { messages: twilioMessages, loading: twilioLoading, error: twilioError, refetch: refetchTwilio, lastFetched } = useTwilioMessages();

  // Build a leadId → lead map for the all-messages view
  const leadMap = React.useMemo(() => {
    const m: Record<string, { name: string; phone: string }> = {};
    for (const l of leads) m[l.id] = { name: l.name, phone: l.phone };
    return m;
  }, [leads]);

  // Build a phone (last-10-digits) → leadId map for By Lead tag display
  const phoneToLeadId = React.useMemo(() => {
    const m: Record<string, string> = {};
    for (const l of leads) {
      if (l.phone) {
        const digits = l.phone.replace(/\D/g, '');
        m[digits.slice(-10)] = l.id;
      }
    }
    return m;
  }, [leads]);

  // ── NEW PHONE-BASED TAG LOGIC ──
  // Helper: normalize any phone string to its last 10 digits
  const normPhone = (phone: string) => phone.replace(/\D/g, '').slice(-10);

  // Build a set of contact phones (last-10-digits) that have sent at least one
  // inbound Twilio message. Derived purely from twilioMessages — no lead lookup needed.
  const inboundPhones = React.useMemo(() => {
    const s = new Set<string>();
    for (const msg of twilioMessages) {
      if (msg.direction === 'in') {
        s.add(normPhone(msg.from));
      }
    }
    return s;
  }, [twilioMessages]);

  // Get tag for a Twilio message using phone-based matching only.
  // 1. Determine the contact phone (sender for inbound, recipient for outbound).
  // 2. Check if that phone has ever sent an inbound message (inboundPhones).
  // 3. Check if that phone is enrolled in a drip campaign (dripPhones).
  const getTwilioMsgTag = useCallback((msg: TwilioApiMessage): 'drip' | 'organic' | null => {
    const contactPhone = normPhone(msg.direction === 'in' ? msg.from : msg.to);
    if (!inboundPhones.has(contactPhone)) return null;
    return dripPhones.has(contactPhone) ? 'drip' : 'organic';
  }, [inboundPhones, dripPhones]);

  // Get tag for a lead in the By Lead tab.
  // Looks up the lead's phone in inboundPhones and dripPhones.
  const getLeadTag = useCallback((leadId: string): 'drip' | 'organic' | null => {
    const lead = leads.find((l) => l.id === leadId);
    if (!lead?.phone) return null;
    const phone = normPhone(lead.phone);
    if (!inboundPhones.has(phone)) return null;
    return dripPhones.has(phone) ? 'drip' : 'organic';
  }, [leads, inboundPhones, dripPhones]);

  const fetchLastMessages = useCallback(async (leadIds: string[]) => {
    if (!leadIds.length) return;
    const supabase = createClient();
    const { data } = await supabase
      .from('chat_messages')
      .select('lead_id, text, direction, created_at')
      .in('lead_id', leadIds)
      .order('created_at', { ascending: false });

    if (!data) return;
    const map: Record<string, LastMsgEntry> = {};
    for (const row of data) {
      const lid = row.lead_id as string;
      if (!map[lid]) {
        map[lid] = {
          text: row.text as string,
          direction: row.direction as 'in' | 'out',
          createdAt: row.created_at as string,
        };
      }
    }
    setLastMsgMap(map);
  }, []);

  useEffect(() => {
    if (!leadsLoading && leads.length > 0) {
      fetchLastMessages(leads.map((l) => l.id));
    }
  }, [leads, leadsLoading, fetchLastMessages]);

  useEffect(() => {
    if (!activeLeadId || messages.length === 0) return;
    const last = messages[messages.length - 1];
    setLastMsgMap((prev) => ({
      ...prev,
      [activeLeadId]: { text: last.text, direction: last.direction, createdAt: last.createdAt },
    }));
  }, [messages, activeLeadId]);

  useEffect(() => {
    if (!activeLeadId && leads.length > 0 && mainTab === 'conversations') {
      setActiveLeadId(leads[0].id);
    }
  }, [leads, activeLeadId, mainTab]);

  useEffect(() => {
    if (viewMode === 'chat') {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages.length, activeLeadId, viewMode]);

  const filteredLeads = leads.filter((l) =>
    l.name.toLowerCase().includes(searchConv.toLowerCase()) ||
    l.phone.includes(searchConv)
  );

  const tagFilteredLeads = leadTagFilter === 'all'
    ? filteredLeads
    : filteredLeads.filter((l) => getLeadTag(l.id) === leadTagFilter);

  const activeLead = leads.find((l) => l.id === activeLeadId);
  const filteredMessages = filterDir === 'all' ? messages : messages.filter(m => m.direction === filterDir);

  // Filter Twilio messages by direction and search
  const filteredTwilioMessages = useMemo(() => {
    let msgs = twilioMessages;
    if (allMsgFilter !== 'all') msgs = msgs.filter(m => m.direction === allMsgFilter);
    if (hideUndelivered) msgs = msgs.filter(m => m.status !== 'undelivered');
    if (allMsgSearch.trim()) {
      const q = allMsgSearch.toLowerCase();
      msgs = msgs.filter(m =>
        m.body.toLowerCase().includes(q) ||
        m.from.toLowerCase().includes(q) ||
        m.to.toLowerCase().includes(q)
      );
    }
    if (leadTagFilter !== 'all') {
      msgs = msgs.filter(m => getTwilioMsgTag(m) === leadTagFilter);
    }
    return msgs;
  }, [twilioMessages, allMsgFilter, allMsgSearch, leadTagFilter, getTwilioMsgTag, hideUndelivered]);

  const twilioSent = twilioMessages.filter(m => m.direction === 'out').length;
  const twilioReceived = twilioMessages.filter(m => m.direction === 'in').length;
  const twilioDelivered = twilioMessages.filter(m => m.status === 'delivered').length;
  const twilioStatus = twilioMessages.filter(m => m.status !== undefined && m.status !== null && m.status !== '').length;
  const twilioRead = twilioMessages.filter(m => m.status === 'read').length;

  // Derive the phone number to reply to from a Twilio message
  function getReplyToPhone(msg: TwilioApiMessage): string {
    // For incoming messages, reply to the sender (msg.from)
    // For outgoing messages, reply to the recipient (msg.to)
    return msg.direction === 'in' ? msg.from : msg.to;
  }

  const handleReplySelect = (msg: TwilioApiMessage) => {
    setReplyTarget(msg);
    setReplyTemplateKey('');
    setReplyAttachment(null);
  };

  const handleReplySend = async () => {
    if (!replyTemplateKey && !replyAttachment) return;
    if (!replyTarget) return;
    setIsReplying(true);
    try {
      const toPhone = getReplyToPhone(replyTarget);
      let mediaUrl: string | undefined;

      if (replyAttachment) {
        setIsUploadingMedia(true);
        try {
          const fd = new FormData();
          fd.append('file', replyAttachment);
          const uploadRes = await fetch('/api/twilio/upload-media', { method: 'POST', body: fd });
          const uploadData = await uploadRes.json() as { success: boolean; url?: string; error?: string };
          if (!uploadData.success || !uploadData.url) {
            toast.error(uploadData.error ?? 'Failed to upload media');
            setIsReplying(false);
            setIsUploadingMedia(false);
            return;
          }
          mediaUrl = uploadData.url;
        } catch (uploadErr) {
          toast.error('Network error — could not upload attachment. Check your connection and try again.');
          setIsReplying(false);
          setIsUploadingMedia(false);
          return;
        } finally {
          setIsUploadingMedia(false);
        }
      }

      if (!replyTemplateKey) {
        toast.error('Please select a template before sending.');
        setIsReplying(false);
        return;
      }

      // Find lead info for the recipient phone
      const normalizedTo = toPhone.replace(/^whatsapp:/i, '').trim();
      const matchedLead = leads.find((l) => {
        const lp = l.phone.replace(/\s/g, '');
        return normalizedTo.includes(lp) || lp.includes(normalizedTo.replace(/^\+/, ''));
      });

      const res = await fetch('/api/twilio/send', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'send_template',
          templateKey: replyTemplateKey,
          lead: {
            name: matchedLead?.name ?? '',
            phone: toPhone,
            neetScore: matchedLead?.neetScore,
            stage: matchedLead?.stage,
          },
          ...(mediaUrl ? { mediaUrl } : {}),
        }),
      });
      const data = await res.json() as { success: boolean; error?: string };
      if (data.success) {
        toast.success(`Template sent to ${normalizedTo}`);
      } else {
        toast.error(data.error ?? 'Failed to send template');
      }
      setReplyTemplateKey('');
      setReplyTarget(null);
      setReplyAttachment(null);
      setTimeout(() => refetchTwilio(), 1000);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Unknown error';
      toast.error(`Network error — could not send: ${msg}`);
    } finally {
      setIsReplying(false);
      setIsUploadingMedia(false);
    }
  };

  const handleSend = async () => {
    if (!activeTemplateName || !activeLeadId) return;
    setIsSending(true);
    try {
      const res = await fetch('/api/twilio/send', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'send_template',
          templateKey: activeTemplateName,
          lead: {
            name: activeLead?.name ?? '',
            phone: formatPhone(activeLead?.phone.replace(/\s/g, '') ?? ''),
            neetScore: activeLead?.neetScore,
            stage: activeLead?.stage,
          },
        }),
      });
      const data = await res.json() as { success: boolean; error?: string };
      if (data.success) {
        toast.success('Template sent via Twilio WhatsApp');
      } else {
        toast.error(data.error ?? 'Failed to send template');
      }
      setTimeout(() => { refetch && refetch(); refetchTwilio(); }, 800);
    } catch {
      toast.error('Network error — could not send template');
    } finally {
      setIsSending(false);
      setInput('');
      setActiveTemplateName(null);
      setShowTemplates(false);
    }
  };

  const handleTemplateUse = (tpl: typeof approvedTemplates[0]) => {
    setActiveTemplateName(tpl.name);
    setShowTemplates(false);
  };

  const handleStageTrigger = async (stage: string) => {
    if (!activeLead) return;
    try {
      const res = await fetch('/api/twilio/send', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'stage_trigger',
          lead: {
            name: activeLead.name,
            phone: formatPhone(activeLead.phone.replace(/\s/g, '')),
            neetScore: activeLead.neetScore,
            stage: activeLead.stage,
          },
          newStage: stage,
        }),
      });
      const data = await res.json() as { success: boolean; error?: string };
      if (data.success) {
        toast.success(`Stage trigger sent for "${stage}"`);
      } else {
        toast.info(`Stage trigger queued (configure Twilio for live delivery)`);
      }
    } catch {
      toast.info(`Stage trigger queued (configure Twilio for live delivery)`);
    }
  };

  if (leadsLoading) {
    return (
      <div className="flex h-[calc(100vh-6rem)] rounded-xl border border-border overflow-hidden bg-card items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
          <p className="text-sm text-muted-foreground">Connecting to live chat…</p>
        </div>
      </div>
    );
  }

  const sentCount = messages.filter(m => m.direction === 'out').length;
  const receivedCount = messages.filter(m => m.direction === 'in').length;

  return (
    <div className="flex h-[calc(100vh-6rem)] rounded-xl border border-border overflow-hidden bg-card">

      {/* ── Left: Sidebar ── */}
      <div className="w-72 shrink-0 border-r border-border flex flex-col bg-card">
        <div className="p-4 border-b border-border">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-base font-bold text-foreground">WhatsApp Chat</h2>
            <span className="inline-flex items-center gap-1.5 text-xs text-emerald-600 font-semibold bg-emerald-50 px-2 py-0.5 rounded-full">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse inline-block" />
              Live
            </span>
          </div>
          {/* Main tab switcher */}
          <div className="flex items-center bg-muted rounded-lg p-0.5 border border-border mb-3">
            <button
              onClick={() => setMainTab('all')}
              className={`flex-1 flex items-center justify-center gap-1.5 py-1.5 rounded-md text-xs font-semibold transition-all ${
                mainTab === 'all' ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              <Globe size={12} />
              All Messages
            </button>
            <button
              onClick={() => { setMainTab('conversations'); if (!activeLeadId && leads.length > 0) setActiveLeadId(leads[0].id); }}
              className={`flex-1 flex items-center justify-center gap-1.5 py-1.5 rounded-md text-xs font-semibold transition-all ${
                mainTab === 'conversations' ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              <MessageSquare size={12} />
              By Lead
            </button>
          </div>
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <input
              type="text"
              placeholder={mainTab === 'all' ? 'Search messages...' : 'Search conversations...'}
              value={mainTab === 'all' ? allMsgSearch : searchConv}
              onChange={(e) => mainTab === 'all' ? setAllMsgSearch(e.target.value) : setSearchConv(e.target.value)}
              className="w-full pl-8 pr-3 py-2 text-sm border border-border rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-ring"
            />
          </div>
        </div>

        {mainTab === 'all' ? (
          /* All Messages sidebar stats */
          <div className="flex-1 overflow-y-auto p-4 space-y-3">
            {/* Lead tag filter pills for All Messages */}
            <div className="flex items-center gap-1.5 flex-wrap">
              <button
                onClick={() => setLeadTagFilter('all')}
                className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-semibold border transition-all ${
                  leadTagFilter === 'all' ? 'bg-foreground text-background border-foreground' : 'bg-card text-muted-foreground border-border hover:border-foreground/40 hover:text-foreground'
                }`}
              >
                All
              </button>
              <button
                onClick={() => setLeadTagFilter('drip')}
                className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-semibold border transition-all ${
                  leadTagFilter === 'drip' ? 'bg-violet-600 text-white border-violet-600' : 'bg-violet-50 text-violet-700 border-violet-200 hover:bg-violet-100'
                }`}
              >
                <span className="w-1.5 h-1.5 rounded-full bg-current inline-block" />
                Responded to Drip
              </button>
              <button
                onClick={() => setLeadTagFilter('organic')}
                className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-semibold border transition-all ${
                  leadTagFilter === 'organic' ? 'bg-emerald-600 text-white border-emerald-600' : 'bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-100'
                }`}
              >
                <span className="w-1.5 h-1.5 rounded-full bg-current inline-block" />
                Organic Inbound
              </button>
            </div>
            <div className="bg-muted/40 rounded-xl p-3 border border-border">
              <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wide mb-2">Twilio Message Stats</p>
              <div className="grid grid-cols-3 gap-2 text-center">
                <div>
                  <p className="text-lg font-bold text-foreground">{twilioMessages.length}</p>
                  <p className="text-[10px] text-muted-foreground">Total</p>
                </div>
                <div>
                  <p className="text-lg font-bold text-primary">{twilioSent}</p>
                  <p className="text-[10px] text-muted-foreground">Outgoing</p>
                </div>
                <div>
                  <p className="text-lg font-bold text-emerald-600">{twilioReceived}</p>
                  <p className="text-[10px] text-muted-foreground">Incoming</p>
                </div>
              </div>
            </div>
            <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-3">
              <p className="text-[11px] font-semibold text-emerald-700 mb-1">✅ Direct from Twilio</p>
              <p className="text-[11px] text-emerald-600 leading-relaxed">
                Messages are fetched directly from the Twilio API — no webhook required. Auto-refreshes every 30 seconds.
              </p>
              {lastFetched && (
                <p className="text-[10px] text-emerald-500 mt-1">
                  Last updated: {lastFetched.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: true })}
                </p>
              )}
            </div>
            <button
              onClick={() => refetchTwilio()}
              disabled={twilioLoading}
              className="w-full flex items-center justify-center gap-2 py-2 rounded-lg border border-border text-xs font-medium text-muted-foreground hover:bg-muted transition-colors disabled:opacity-50"
            >
              <RefreshCw size={13} className={twilioLoading ? 'animate-spin' : ''} />
              {twilioLoading ? 'Fetching...' : 'Refresh from Twilio'}
            </button>
          </div>
        ) : (
          /* Conversations list */
          <div className="flex-1 flex flex-col overflow-hidden">
          {/* Lead tag filter pills */}
          <div className="flex items-center gap-1.5 px-3 py-2 border-b border-border/60 bg-muted/20 flex-wrap">
            <button
              onClick={() => setLeadTagFilter('all')}
              className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-semibold border transition-all ${
                leadTagFilter === 'all' ?'bg-foreground text-background border-foreground' :'bg-card text-muted-foreground border-border hover:border-foreground/40 hover:text-foreground'
              }`}
            >
              All
            </button>
            <button
              onClick={() => setLeadTagFilter('drip')}
              className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-semibold border transition-all ${
                leadTagFilter === 'drip' ?'bg-violet-600 text-white border-violet-600' :'bg-violet-50 text-violet-700 border-violet-200 hover:bg-violet-100'
              }`}
            >
              <span className="w-1.5 h-1.5 rounded-full bg-current inline-block" />
              Responded to Drip
            </button>
            <button
              onClick={() => setLeadTagFilter('organic')}
              className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-semibold border transition-all ${
                leadTagFilter === 'organic' ?'bg-emerald-600 text-white border-emerald-600' :'bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-100'
              }`}
            >
              <span className="w-1.5 h-1.5 rounded-full bg-current inline-block" />
              Organic Inbound
            </button>
          </div>
          <div className="flex-1 overflow-y-auto">
            {tagFilteredLeads.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-32 gap-2">
                <MessageSquare size={24} className="text-muted-foreground/40" />
                <p className="text-xs text-muted-foreground">No conversations found</p>
              </div>
            ) : (
              tagFilteredLeads.map((lead) => {
                const lastMsg = lastMsgMap[lead.id];
                return (
                  <button
                    key={lead.id}
                    onClick={() => setActiveLeadId(lead.id)}
                    className={`w-full flex items-start gap-3 px-4 py-3 text-left border-b border-border/40 transition-colors hover:bg-muted/40 ${
                      activeLeadId === lead.id ? 'bg-primary/5 border-l-[3px] border-l-primary' : 'border-l-[3px] border-l-transparent'
                    }`}
                  >
                    <div className="relative shrink-0 mt-0.5">
                      <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center text-sm font-bold text-primary">
                        {lead.name.split(' ').map((n: string) => n[0]).join('').slice(0, 2)}
                      </div>
                      {!lead.optedIn && (
                        <span className="absolute -bottom-0.5 -right-0.5 w-3.5 h-3.5 bg-red-500 rounded-full border-2 border-card flex items-center justify-center">
                          <X size={6} className="text-white" />
                        </span>
                      )}
                      {lead.optedIn && (
                        <span className="absolute -bottom-0.5 -right-0.5 w-3.5 h-3.5 bg-emerald-500 rounded-full border-2 border-card" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between mb-0.5">
                        <span className="text-sm font-semibold text-foreground truncate">{lead.name}</span>
                        {lastMsg && (
                          <span className="text-[10px] text-muted-foreground shrink-0 ml-1">{formatMsgTime(lastMsg.createdAt)}</span>
                        )}
                      </div>
                      {lastMsg ? (
                        <div className="flex items-center gap-1">
                          {lastMsg.direction === 'out' ? (
                            <ArrowUpRight size={10} className="text-primary shrink-0" />
                          ) : (
                            <ArrowDownLeft size={10} className="text-emerald-500 shrink-0" />
                          )}
                          <p className="text-xs text-muted-foreground truncate">{lastMsg.text}</p>
                        </div>
                      ) : (
                        <p className="text-xs text-muted-foreground truncate">{lead.stage}</p>
                      )}
                      <div className="flex items-center gap-1.5 mt-1 flex-wrap">
                        <StageBadge stage={lead.stage as LeadStage} />
                        {getLeadTag(lead.id) === 'drip' && (
                          <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-[9px] font-semibold bg-violet-50 text-violet-700 border border-violet-200">
                            <span className="w-1 h-1 rounded-full bg-violet-500 inline-block" />
                            Responded to Drip
                          </span>
                        )}
                        {getLeadTag(lead.id) === 'organic' && (
                          <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-[9px] font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200">
                            <span className="w-1 h-1 rounded-full bg-emerald-500 inline-block" />
                            Organic Inbound
                          </span>
                        )}
                      </div>
                    </div>
                  </button>
                );
              })
            )}
          </div>
          </div>
        )}
      </div>

      {/* ── Center: Main Content ── */}
      {mainTab === 'all' ? (
        /* ── ALL MESSAGES — DIRECT FROM TWILIO API ── */
        <div className="flex-1 flex flex-col min-w-0">
          {/* Header */}
          <div className="flex items-center justify-between px-5 py-3 border-b border-border bg-card shadow-sm">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-full bg-emerald-100 flex items-center justify-center">
                <Globe size={18} className="text-emerald-600" />
              </div>
              <div>
                <p className="text-sm font-bold text-foreground">All WhatsApp Messages</p>
                <p className="text-xs text-muted-foreground">Pulled directly from Twilio API · auto-refreshes every 30s</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {/* Direction filter */}
              <div className="flex items-center bg-muted rounded-lg p-0.5 border border-border">
                {(['all', 'in', 'out'] as const).map((d) => (
                  <button
                    key={d}
                    onClick={() => setAllMsgFilter(d)}
                    className={`px-2.5 py-1 rounded-md text-xs font-semibold transition-all ${
                      allMsgFilter === d ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'
                    }`}
                  >
                    {d === 'all' ? 'All' : d === 'in' ? '↙ Incoming' : '↗ Outgoing'}
                  </button>
                ))}
              </div>
              <button
                onClick={() => refetchTwilio()}
                disabled={twilioLoading}
                className="p-2 rounded-lg hover:bg-muted text-muted-foreground hover:text-foreground transition-colors disabled:opacity-50"
                title="Refresh from Twilio"
              >
                <RefreshCw size={15} className={twilioLoading ? 'animate-spin' : ''} />
              </button>
            </div>
          </div>

          {/* Stats bar */}
          <div className="flex items-center gap-4 px-5 py-2 border-b border-border bg-muted/20 text-xs flex-wrap">
            <span className="text-muted-foreground font-medium">
              Showing: <span className="text-foreground font-bold">{filteredTwilioMessages.length}</span>
              {filteredTwilioMessages.length !== twilioMessages.length && <span className="text-muted-foreground"> of {twilioMessages.length}</span>}
            </span>
            <span className="flex items-center gap-1 text-muted-foreground font-medium">
              <ArrowUpRight size={11} className="text-primary" />
              Outgoing: <span className="text-primary font-bold ml-0.5">{twilioSent}</span>
            </span>
            <span className="flex items-center gap-1 text-muted-foreground font-medium">
              <ArrowDownLeft size={11} className="text-emerald-600" />
              Incoming: <span className="text-emerald-600 font-bold ml-0.5">{twilioReceived}</span>
            </span>
            <span className="flex items-center gap-1 text-muted-foreground font-medium">
              <CheckCheck size={11} className="text-blue-500" />
              Delivered: <span className="text-blue-600 font-bold ml-0.5">{twilioDelivered}</span>
            </span>
            <span className="flex items-center gap-1 text-muted-foreground font-medium">
              <CheckCheck size={11} className="text-amber-500" />
              Status: <span className="text-amber-600 font-bold ml-0.5">{twilioStatus}</span>
            </span>
            <span className="flex items-center gap-1 text-muted-foreground font-medium">
              <CheckCheck size={11} className="text-violet-500" />
              Read: <span className="text-violet-600 font-bold ml-0.5">{twilioRead}</span>
            </span>
            <button
              onClick={() => setHideUndelivered(prev => !prev)}
              className={`ml-auto flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold border transition-all ${
                hideUndelivered
                  ? 'bg-gray-500 text-white border-gray-500' :'bg-gray-100 text-gray-600 border-gray-300 hover:bg-gray-200'
              }`}
              title={hideUndelivered ? 'Show undelivered messages' : 'Hide undelivered messages'}
            >
              <AlertCircle size={11} />
              {hideUndelivered ? 'Showing Undelivered' : 'Hide Undelivered'}
            </button>
            {twilioLoading && (
              <span className="flex items-center gap-1.5 text-muted-foreground">
                <div className="w-3 h-3 border border-primary border-t-transparent rounded-full animate-spin" />
                Fetching from Twilio…
              </span>
            )}
          </div>

          {/* Table */}
          <div className="flex-1 overflow-y-auto bg-background">
            {twilioError ? (
              <div className="flex flex-col items-center justify-center h-full gap-3">
                <div className="w-14 h-14 rounded-full bg-red-50 flex items-center justify-center">
                  <AlertCircle size={24} className="text-red-500" />
                </div>
                <p className="text-sm text-foreground font-medium">Failed to fetch from Twilio</p>
                <p className="text-xs text-muted-foreground max-w-xs text-center">{twilioError}</p>
                <button
                  onClick={() => refetchTwilio()}
                  className="flex items-center gap-2 px-4 py-2 rounded-lg bg-primary/10 text-primary text-xs font-medium hover:bg-primary/20 transition-colors"
                >
                  <RefreshCw size={13} />
                  Retry
                </button>
              </div>
            ) : twilioLoading && twilioMessages.length === 0 ? (
              <div className="flex items-center justify-center h-full">
                <div className="flex flex-col items-center gap-3">
                  <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                  <p className="text-xs text-muted-foreground">Fetching messages from Twilio API…</p>
                </div>
              </div>
            ) : filteredTwilioMessages.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full gap-3">
                <div className="w-14 h-14 rounded-full bg-muted flex items-center justify-center">
                  <MessageSquare size={24} className="text-muted-foreground/50" />
                </div>
                <p className="text-sm text-muted-foreground font-medium">No messages found</p>
                <p className="text-xs text-muted-foreground/70">
                  {twilioMessages.length === 0
                    ? 'No WhatsApp messages found in your Twilio account' :'Try adjusting your search or filter'}
                </p>
                <button
                  onClick={() => refetchTwilio()}
                  className="flex items-center gap-2 px-4 py-2 rounded-lg bg-primary/10 text-primary text-xs font-medium hover:bg-primary/20 transition-colors"
                >
                  <RefreshCw size={13} />
                  Refresh
                </button>
              </div>
            ) : (
              <table className="w-full text-sm border-collapse">
                <thead className="sticky top-0 z-10">
                  <tr className="bg-muted/60 border-b border-border">
                    <th className="text-left px-4 py-2.5 text-[11px] font-semibold text-muted-foreground uppercase tracking-wide w-36">Date &amp; Time</th>
                    <th className="text-left px-3 py-2.5 text-[11px] font-semibold text-muted-foreground uppercase tracking-wide w-24">Direction</th>
                    <th className="text-left px-3 py-2.5 text-[11px] font-semibold text-muted-foreground uppercase tracking-wide w-40">From</th>
                    <th className="text-left px-3 py-2.5 text-[11px] font-semibold text-muted-foreground uppercase tracking-wide w-40">To</th>
                    <th className="text-left px-3 py-2.5 text-[11px] font-semibold text-muted-foreground uppercase tracking-wide">Message</th>
                    <th className="text-left px-3 py-2.5 text-[11px] font-semibold text-muted-foreground uppercase tracking-wide w-28">Status</th>
                    <th className="text-left px-3 py-2.5 text-[11px] font-semibold text-muted-foreground uppercase tracking-wide w-20">Reply</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredTwilioMessages.map((msg, idx) => {
                    const isOut = msg.direction === 'out';
                    const isSelected = replyTarget?.sid === msg.sid;
                    return (
                      <tr
                        key={msg.sid}
                        className={`border-b border-border/60 transition-colors hover:bg-muted/30 ${
                          idx % 2 === 0 ? 'bg-card' : 'bg-muted/10'
                        } ${!isOut ? 'border-l-2 border-l-emerald-400' : ''} ${isSelected ? 'ring-2 ring-inset ring-primary/30 bg-primary/5' : ''}`}
                      >
                        <td className="px-4 py-3 align-top">
                          <span className="text-xs text-muted-foreground font-mono whitespace-nowrap">
                            {formatMsgDateTime(msg.createdAt)}
                          </span>
                        </td>
                        <td className="px-3 py-3 align-top">
                          {isOut ? (
                            <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md text-[11px] font-semibold bg-primary/10 text-primary border border-primary/20">
                              <ArrowUpRight size={11} />
                              Outgoing
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md text-[11px] font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200">
                              <ArrowDownLeft size={11} />
                              Incoming
                            </span>
                          )}
                        </td>
                        <td className="px-3 py-3 align-top">
                          <div className="flex flex-col gap-0.5">
                            <span className="text-[10px] text-muted-foreground font-medium uppercase tracking-wide">WhatsApp</span>
                            <span className="text-xs text-foreground font-mono truncate max-w-[150px]">{msg.from}</span>
                          </div>
                        </td>
                        <td className="px-3 py-3 align-top">
                          <div className="flex flex-col gap-0.5">
                            <span className="text-[10px] text-muted-foreground font-medium uppercase tracking-wide">WhatsApp</span>
                            <span className="text-xs text-foreground font-mono truncate max-w-[150px]">{msg.to}</span>
                          </div>
                        </td>
                        <td className="px-3 py-3 align-top max-w-xs">
                          <p className="text-xs text-foreground leading-relaxed line-clamp-3 whitespace-pre-wrap break-words">
                            {msg.body}
                          </p>
                        </td>
                        <td className="px-3 py-3 align-top">
                          <StatusBadge status={msg.status as ChatMessage['status']} direction={msg.direction} />
                        </td>
                        <td className="px-3 py-3 align-top">
                          <button
                            onClick={() => isSelected ? setReplyTarget(null) : handleReplySelect(msg)}
                            className={`inline-flex items-center gap-1 px-2 py-1 rounded-md text-[11px] font-semibold border transition-colors ${
                              isSelected
                                ? 'bg-primary text-primary-foreground border-primary'
                                : 'bg-muted text-muted-foreground border-border hover:bg-primary/10 hover:text-primary hover:border-primary/30'
                            }`}
                            title="Reply to this contact"
                          >
                            <Reply size={11} />
                            {isSelected ? 'Cancel' : 'Reply'}
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>

          {/* ── Reply Panel ── */}
          {replyTarget && (
            <div className="border-t border-border bg-card shadow-lg">
              {/* Context strip */}
              <div className="flex items-start gap-3 px-4 py-2.5 bg-muted/40 border-b border-border">
                <Reply size={14} className="text-primary mt-0.5 shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wide mb-0.5">
                    Replying to {replyTarget.direction === 'in' ? replyTarget.from : replyTarget.to}
                  </p>
                  <p className="text-xs text-foreground truncate">{replyTarget.body}</p>
                </div>
                <button
                  onClick={() => { setReplyTarget(null); setReplyTemplateKey(''); setReplyAttachment(null); }}
                  className="text-muted-foreground hover:text-foreground transition-colors shrink-0"
                  title="Cancel reply"
                >
                  <X size={14} />
                </button>
              </div>

              {/* Attachment preview strip */}
              {replyAttachment && (
                <div className="flex items-center gap-2 px-4 py-2 bg-primary/5 border-b border-border">
                  <Paperclip size={13} className="text-primary shrink-0" />
                  <span className="text-xs text-foreground truncate flex-1">{replyAttachment.name}</span>
                  <span className="text-[10px] text-muted-foreground shrink-0">
                    {(replyAttachment.size / 1024).toFixed(1)} KB
                  </span>
                  <button
                    onClick={() => setReplyAttachment(null)}
                    className="text-muted-foreground hover:text-destructive transition-colors shrink-0"
                    title="Remove attachment"
                  >
                    <X size={12} />
                  </button>
                </div>
              )}

              {/* Template selector row */}
              <div className="flex items-center gap-2 px-4 py-3">
                {/* Hidden file input */}
                <input
                  ref={replyFileInputRef}
                  type="file"
                  accept="image/*,video/*,audio/*,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt"
                  className="hidden"
                  onChange={(e) => {
                    const file = e.target.files?.[0] ?? null;
                    setReplyAttachment(file);
                    e.target.value = '';
                  }}
                />
                {/* Attachment button */}
                <button
                  onClick={() => replyFileInputRef.current?.click()}
                  className="p-2.5 rounded-xl border border-border bg-background hover:bg-muted text-muted-foreground hover:text-foreground transition-colors shrink-0"
                  title="Attach media or file"
                  disabled={isReplying}
                >
                  <Paperclip size={16} />
                </button>

                {/* Template dropdown */}
                <div className="flex-1 relative">
                  <FileText size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
                  <ChevronDown size={13} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
                  <select
                    value={replyTemplateKey}
                    onChange={(e) => setReplyTemplateKey(e.target.value)}
                    disabled={isReplying}
                    className="w-full pl-8 pr-8 py-2.5 text-sm border border-border rounded-xl bg-background focus:outline-none focus:ring-2 focus:ring-ring appearance-none disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <option value="">— Select an approved template —</option>
                    {approvedTemplates.length === 0 && (
                      <option disabled value="">No approved templates found</option>
                    )}
                    {approvedTemplates.map((tpl) => (
                      <option key={tpl.id} value={tpl.name}>{tpl.label}</option>
                    ))}
                  </select>
                </div>

                <button
                  onClick={handleReplySend}
                  disabled={!replyTemplateKey || isReplying}
                  className="p-2.5 rounded-xl bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors shrink-0"
                  title="Send template"
                >
                  {isReplying ? (
                    <div className="w-4 h-4 border-2 border-primary-foreground border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <Send size={16} />
                  )}
                </button>
              </div>

              {/* Template preview */}
              {replyTemplateKey && (() => {
                const tpl = approvedTemplates.find((t) => t.name === replyTemplateKey);
                return tpl ? (
                  <div className="mx-4 mb-3 px-3 py-2 bg-primary/5 border border-primary/20 rounded-lg">
                    <p className="text-[10px] font-semibold text-primary/70 uppercase tracking-wide mb-1">Template Preview</p>
                    <p className="text-xs text-foreground leading-relaxed">{tpl.body}</p>
                  </div>
                ) : null;
              })()}

              {isUploadingMedia && (
                <div className="px-4 pb-2 text-[11px] text-muted-foreground flex items-center gap-1.5">
                  <div className="w-3 h-3 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                  Uploading media…
                </div>
              )}
            </div>
          )}
        </div>
      ) : activeLead ? (
        /* ── BY-LEAD CONVERSATION VIEW ── */
        <div className="flex-1 flex flex-col min-w-0">
          {/* Chat header */}
          <div className="flex items-center justify-between px-5 py-3 border-b border-border bg-card shadow-sm">
            <div className="flex items-center gap-3">
              <div className="relative">
                <div className="w-9 h-9 rounded-full bg-primary/10 flex items-center justify-center text-sm font-bold text-primary">
                  {activeLead.name.split(' ').map((n: string) => n[0]).join('').slice(0, 2)}
                </div>
                {activeLead.optedIn && (
                  <span className="absolute -bottom-0.5 -right-0.5 w-3 h-3 bg-emerald-500 rounded-full border-2 border-card" />
                )}
              </div>
              <div>
                <p className="text-sm font-bold text-foreground">{activeLead.name}</p>
                <p className="text-xs text-muted-foreground">{formatPhone(activeLead.phone)} · NEET {activeLead.neetScore}</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <div className="flex items-center bg-muted rounded-lg p-0.5 border border-border">
                <button
                  onClick={() => setViewMode('log')}
                  className={`px-2.5 py-1 rounded-md text-xs font-semibold transition-all ${
                    viewMode === 'log' ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  Log View
                </button>
                <button
                  onClick={() => setViewMode('chat')}
                  className={`px-2.5 py-1 rounded-md text-xs font-semibold transition-all ${
                    viewMode === 'chat' ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  Chat View
                </button>
              </div>
              <button
                title="Send stage trigger"
                onClick={() => handleStageTrigger(activeLead.stage)}
                className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-primary/10 text-primary hover:bg-primary/20 transition-colors text-xs font-medium"
              >
                <Zap size={13} />
                Stage Trigger
              </button>
              <button
                title="Refresh messages"
                onClick={() => refetch && refetch()}
                className="p-2 rounded-lg hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
              >
                <RefreshCw size={15} />
              </button>
              <button title="Call lead" className="p-2 rounded-lg hover:bg-muted text-muted-foreground hover:text-foreground transition-colors">
                <Phone size={16} />
              </button>
              <button title="More options" className="p-2 rounded-lg hover:bg-muted text-muted-foreground hover:text-foreground transition-colors">
                <MoreVertical size={16} />
              </button>
            </div>
          </div>

          {/* Stats bar */}
          <div className="flex items-center gap-4 px-5 py-2 border-b border-border bg-muted/20 text-xs">
            <span className="text-muted-foreground font-medium">
              Total: <span className="text-foreground font-bold">{messages.length}</span>
            </span>
            <span className="flex items-center gap-1 text-muted-foreground font-medium">
              <ArrowUpRight size={11} className="text-primary" />
              Outgoing: <span className="text-primary font-bold ml-0.5">{sentCount}</span>
            </span>
            <span className="flex items-center gap-1 text-muted-foreground font-medium">
              <ArrowDownLeft size={11} className="text-emerald-600" />
              Incoming: <span className="text-emerald-600 font-bold ml-0.5">{receivedCount}</span>
            </span>
            <div className="ml-auto flex items-center gap-2">
              <div className="flex items-center gap-1 bg-card border border-border rounded-lg p-0.5">
                {(['all', 'in', 'out'] as const).map((d) => (
                  <button
                    key={d}
                    onClick={() => setFilterDir(d)}
                    className={`px-2 py-0.5 rounded text-[11px] font-semibold transition-all ${
                      filterDir === d ? 'bg-primary text-white' : 'text-muted-foreground hover:text-foreground'
                    }`}
                  >
                    {d === 'all' ? 'All' : d === 'in' ? '↙ Incoming' : '↗ Outgoing'}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* LOG VIEW */}
          {viewMode === 'log' ? (
            <div className="flex-1 overflow-y-auto bg-background">
              {msgsLoading ? (
                <div className="flex items-center justify-center h-full">
                  <div className="flex flex-col items-center gap-3">
                    <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                    <p className="text-xs text-muted-foreground">Loading messages…</p>
                  </div>
                </div>
              ) : filteredMessages.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-full gap-3">
                  <div className="w-14 h-14 rounded-full bg-muted flex items-center justify-center">
                    <MessageSquare size={24} className="text-muted-foreground/50" />
                  </div>
                  <p className="text-sm text-muted-foreground font-medium">No messages yet</p>
                  <p className="text-xs text-muted-foreground/70">Start the conversation by sending a message below</p>
                </div>
              ) : (
                <table className="w-full text-sm border-collapse">
                  <thead className="sticky top-0 z-10">
                    <tr className="bg-muted/60 border-b border-border">
                      <th className="text-left px-4 py-2.5 text-[11px] font-semibold text-muted-foreground uppercase tracking-wide w-36">Date &amp; Time</th>
                      <th className="text-left px-3 py-2.5 text-[11px] font-semibold text-muted-foreground uppercase tracking-wide w-28">Direction</th>
                      <th className="text-left px-3 py-2.5 text-[11px] font-semibold text-muted-foreground uppercase tracking-wide w-36">From</th>
                      <th className="text-left px-3 py-2.5 text-[11px] font-semibold text-muted-foreground uppercase tracking-wide w-36">To</th>
                      <th className="text-left px-3 py-2.5 text-[11px] font-semibold text-muted-foreground uppercase tracking-wide">Message</th>
                      <th className="text-left px-3 py-2.5 text-[11px] font-semibold text-muted-foreground uppercase tracking-wide w-24">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredMessages.map((msg, idx) => {
                      const isOut = msg.direction === 'out';
                      const contactPhone = formatPhone(activeLead.phone);
                      const fromPhone = isOut ? (TWILIO_NUMBER || 'Our WhatsApp') : `whatsapp: ${contactPhone}`;
                      const toPhone = isOut ? `whatsapp: ${contactPhone}` : (TWILIO_NUMBER || 'Our WhatsApp');
                      return (
                        <tr
                          key={msg.id}
                          className={`border-b border-border/60 transition-colors hover:bg-muted/30 ${
                            idx % 2 === 0 ? 'bg-card' : 'bg-muted/10'
                          } ${isOut ? '' : 'border-l-2 border-l-emerald-400'}`}
                        >
                          <td className="px-4 py-3 align-top">
                            <span className="text-xs text-muted-foreground font-mono whitespace-nowrap">
                              {formatMsgDateTime(msg.createdAt)}
                            </span>
                          </td>
                          <td className="px-3 py-3 align-top">
                            {isOut ? (
                              <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md text-[11px] font-semibold bg-primary/10 text-primary border border-primary/20">
                                <ArrowUpRight size={11} />
                                Outgoing
                              </span>
                            ) : (
                              <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md text-[11px] font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200">
                                <ArrowDownLeft size={11} />
                                Incoming
                              </span>
                            )}
                          </td>
                          <td className="px-3 py-3 align-top">
                            <div className="flex flex-col gap-0.5">
                              <span className="text-[10px] text-muted-foreground font-medium uppercase tracking-wide">WhatsApp</span>
                              <span className="text-xs text-foreground font-mono">{fromPhone}</span>
                            </div>
                          </td>
                          <td className="px-3 py-3 align-top">
                            <div className="flex flex-col gap-0.5">
                              <span className="text-[10px] text-muted-foreground font-medium uppercase tracking-wide">WhatsApp</span>
                              <span className="text-xs text-foreground font-mono">{toPhone}</span>
                            </div>
                          </td>
                          <td className="px-3 py-3 align-top max-w-xs">
                            <div className="flex flex-col gap-1">
                              {msg.isTemplate && (
                                <span className="inline-flex items-center gap-1 text-[10px] text-primary/70 font-semibold">
                                  <FileText size={9} />
                                  {msg.templateName}
                                </span>
                              )}
                              <p className="text-xs text-foreground leading-relaxed line-clamp-3 whitespace-pre-wrap break-words">
                                {msg.text}
                              </p>
                            </div>
                          </td>
                          <td className="px-3 py-3 align-top">
                            <StatusBadge status={msg.status} direction={msg.direction} />
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
          ) : (
            /* CHAT VIEW */
            <div className="flex-1 overflow-y-auto p-4 space-y-1 bg-muted/10">
              {msgsLoading ? (
                <div className="flex items-center justify-center h-full">
                  <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                </div>
              ) : filteredMessages.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-full gap-2">
                  <MessageSquare size={32} className="text-muted-foreground/30" />
                  <p className="text-sm text-muted-foreground">No messages yet</p>
                </div>
              ) : (
                groupMessagesByDate(filteredMessages).map(({ date, msgs }) => (
                  <div key={date}>
                    <div className="flex items-center gap-3 my-4">
                      <div className="flex-1 h-px bg-border" />
                      <span className="text-[11px] text-muted-foreground font-medium px-2 py-0.5 bg-muted rounded-full">{date}</span>
                      <div className="flex-1 h-px bg-border" />
                    </div>
                    {msgs.map((msg) => {
                      const isOut = msg.direction === 'out';
                      return (
                        <div key={msg.id} className={`flex items-end gap-2 mb-2 ${isOut ? 'justify-end' : 'justify-start'}`}>
                          {!isOut && (
                            <div className="w-7 h-7 rounded-full bg-emerald-100 flex items-center justify-center text-[11px] font-bold text-emerald-700 shrink-0">
                              {activeLead.name.split(' ').map((n: string) => n[0]).join('').slice(0, 2)}
                            </div>
                          )}
                          <div className={`max-w-[70%] rounded-2xl px-3.5 py-2.5 shadow-sm ${
                            isOut
                              ? 'bg-primary text-primary-foreground rounded-br-sm'
                              : 'bg-card text-foreground border border-border rounded-bl-sm'
                          }`}>
                            {msg.isTemplate && (
                              <div className={`flex items-center gap-1 mb-1 text-[10px] font-semibold ${isOut ? 'text-primary-foreground/70' : 'text-primary/70'}`}>
                                <FileText size={9} />
                                {msg.templateName}
                              </div>
                            )}
                            <p className="text-sm leading-relaxed whitespace-pre-wrap break-words">{msg.text}</p>
                            <div className={`flex items-center gap-1 mt-1 ${isOut ? 'justify-end' : 'justify-start'}`}>
                              <span className={`text-[10px] ${isOut ? 'text-primary-foreground/60' : 'text-muted-foreground'}`}>
                                {formatMsgTime(msg.createdAt)}
                              </span>
                              {isOut && <StatusIcon status={msg.status} />}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ))
              )}
              <div ref={messagesEndRef} />
            </div>
          )}

          {/* Input area */}
          <div className="border-t border-border bg-card p-3">
            {/* Template picker panel */}
            {showTemplates && (
              <div className="mb-3 bg-muted/50 rounded-xl border border-border p-3">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-xs font-semibold text-foreground">Select Approved Template</p>
                  <button onClick={() => setShowTemplates(false)} className="text-muted-foreground hover:text-foreground">
                    <X size={14} />
                  </button>
                </div>
                <div className="space-y-1.5">
                  {approvedTemplates.length === 0 && (
                    <p className="text-xs text-muted-foreground text-center py-2">No approved templates found. Approve templates in Twilio Settings.</p>
                  )}
                  {approvedTemplates.map((tpl) => (
                    <button
                      key={tpl.id}
                      onClick={() => handleTemplateUse(tpl)}
                      className={`w-full text-left px-3 py-2 rounded-lg border transition-all ${
                        activeTemplateName === tpl.name
                          ? 'bg-primary/10 border-primary/30 text-primary' :'hover:bg-card border-transparent hover:border-border'
                      }`}
                    >
                      <p className="text-xs font-semibold text-foreground">{tpl.label}</p>
                      <p className="text-[11px] text-muted-foreground truncate mt-0.5">{tpl.body.slice(0, 60)}…</p>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Selected template preview */}
            {activeTemplateName && (() => {
              const tpl = approvedTemplates.find((t) => t.name === activeTemplateName);
              return tpl ? (
                <div className="mb-2 px-3 py-2 bg-primary/5 rounded-lg border border-primary/20">
                  <div className="flex items-center justify-between mb-1">
                    <div className="flex items-center gap-1.5">
                      <FileText size={12} className="text-primary" />
                      <span className="text-xs text-primary font-semibold">{tpl.label}</span>
                    </div>
                    <button onClick={() => setActiveTemplateName(null)} className="text-primary/60 hover:text-primary">
                      <X size={12} />
                    </button>
                  </div>
                  <p className="text-[11px] text-foreground/80 leading-relaxed">{tpl.body}</p>
                </div>
              ) : null;
            })()}

            {/* No template selected notice */}
            {!activeTemplateName && !showTemplates && (
              <div className="mb-2 px-3 py-2 bg-amber-50 border border-amber-200 rounded-lg flex items-center gap-2">
                <AlertCircle size={13} className="text-amber-600 shrink-0" />
                <p className="text-[11px] text-amber-700">Select an approved template to send a message outside the 24-hour session window.</p>
              </div>
            )}

            <div className="flex items-center gap-2">
              <button
                onClick={() => setShowTemplates(!showTemplates)}
                className={`flex items-center gap-1.5 px-3 py-2.5 rounded-xl border transition-colors shrink-0 text-xs font-semibold ${
                  showTemplates || activeTemplateName
                    ? 'bg-primary/10 border-primary/30 text-primary' :'border-border text-muted-foreground hover:bg-muted'
                }`}
                title="Choose template"
              >
                <Zap size={14} />
                {activeTemplateName ? 'Change' : 'Template'}
              </button>
              <button
                onClick={handleSend}
                disabled={!activeTemplateName || isSending || !activeLead?.optedIn}
                className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm font-semibold"
              >
                {isSending ? (
                  <div className="w-4 h-4 border-2 border-primary-foreground border-t-transparent rounded-full animate-spin" />
                ) : (
                  <>
                    <Send size={15} />
                    Send Template
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      ) : (
        /* No lead selected */
        <div className="flex-1 flex flex-col items-center justify-center gap-4 bg-muted/10">
          <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center">
            <MessageSquare size={28} className="text-muted-foreground/40" />
          </div>
          <div className="text-center">
            <p className="text-sm font-semibold text-foreground">Select a conversation</p>
            <p className="text-xs text-muted-foreground mt-1">Choose a lead from the sidebar to view messages</p>
          </div>
        </div>
      )}
    </div>
  );
}